import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get, post } from "../../lib/request"

const TOKEN_KEY = "auth_token"
const USER_KEY = "auth_user"
const INVITATION_TOKEN_KEY = "invitation_token"
const REMEMBERED_EMAIL_KEY = "auth_remembered_email"

export const checkInvitedUserExists = createAsyncThunk(
  "invitedUser/checkExists",
  async (token, { rejectWithValue }) => {
    try {
      const res = await get(`authn/invited-user-exists/${token}`)
      return res?.data
    } catch (error) {
      console.error("API ERROR")
      console.error("Error Message:", error?.message)
      console.error("Error Details:", error)
      return rejectWithValue(error?.message || "Failed to check invited user")
    }
  },
)

export const signupInvitedUser = createAsyncThunk(
  "invitedUser/signup",
  async (
    {
      email,
      username,
      password,
      token,
    },
    { rejectWithValue }
  ) => {
    try {
      const data = await post(`authn/register/user?token=${token}`, {
        email,
        username,
        password,
      })
      return { signupData: data, email, username, password, token }
    } catch (error) {

      return rejectWithValue(error?.message)
    }
  },
)

export const loginInvitedUser = createAsyncThunk(
  "invitedUser/login",
  async (
    {
      email,
      username,
      password,
      token,
      rememberMe = false,
    },
    { rejectWithValue }
  ) => {
    try {
      const data = await post(
        `authn/login?token=${token}`,
        {
          email,
          username,
          password,
        },
        { skipAuth: true },
      )
      if (data?.accessToken) {
        if (rememberMe) {
          localStorage.setItem(TOKEN_KEY, data.accessToken)

          localStorage.setItem(
            USER_KEY,
            JSON.stringify({
              username: email,
              email,
            })
          )

          localStorage.setItem(
            REMEMBERED_EMAIL_KEY,
            email || ""
          )
        } else {
          sessionStorage.setItem(
            TOKEN_KEY,
            data.accessToken
          )

          sessionStorage.setItem(
            USER_KEY,
            JSON.stringify({
              username: email,
              email,
            })
          )

          localStorage.removeItem(
            REMEMBERED_EMAIL_KEY
          )
        }
      }
      return data
    } catch (error) {
      return rejectWithValue(error?.message)
    }
  },
)

const initialState = {
  userExists: null,
  userData: null,
  token: localStorage.getItem(INVITATION_TOKEN_KEY) || null,
  accessToken: null,
  loading: false,
  error: null,
  loginLoading: false,
  loginError: null,
  signupLoading: false,
  signupError: null,
  authSuccess: false,
  signupCredentials: null,
}

const invitedUserSlice = createSlice({
  name: "invitedUser",
  initialState,
  reducers: {
    clearInvitedUserState: (state) => {
      state.userExists = null
      state.userData = null
      state.loading = false
      state.error = null
      state.loginLoading = false
      state.loginError = null
      state.signupLoading = false
      state.signupError = null
      state.authSuccess = false
      state.signupCredentials = null
      localStorage.removeItem(INVITATION_TOKEN_KEY)
    },
    setInvitationToken: (state, action) => {
      state.token = action.payload
      if (action.payload) {
        localStorage.setItem(INVITATION_TOKEN_KEY, action.payload)
      } else {
        localStorage.removeItem(INVITATION_TOKEN_KEY)
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkInvitedUserExists.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(checkInvitedUserExists.fulfilled, (state, action) => {
        state.loading = false
        state.userExists = action.payload?.invitee !== null
        state.userData = action.payload
      })
      .addCase(checkInvitedUserExists.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || "Failed to verify invitation"
        state.userExists = null
        localStorage.removeItem(INVITATION_TOKEN_KEY)
      })
      .addCase(signupInvitedUser.pending, (state) => {
        state.signupLoading = true
        state.signupError = null
      })
      .addCase(signupInvitedUser.fulfilled, (state, action) => {
        state.signupLoading = false
        state.signupCredentials = {
          email: action.payload.email,
          username: action.payload.username,
          password: action.payload.password,
          token: action.payload.token,
        }
      })
      .addCase(signupInvitedUser.rejected, (state, action) => {
        state.signupLoading = false
        state.signupError = action.payload
      })
      .addCase(loginInvitedUser.pending, (state) => {
        state.loginLoading = true
        state.loginError = null
      })
      .addCase(loginInvitedUser.fulfilled, (state, action) => {
        state.loginLoading = false
        state.accessToken = action.payload?.data?.accessToken
        state.authSuccess = true
        state.signupCredentials = null
      })
      .addCase(loginInvitedUser.rejected, (state, action) => {
        state.loginLoading = false
        state.loginError = action.payload
      })
  },
})

export const { clearInvitedUserState, setInvitationToken } = invitedUserSlice.actions

export default invitedUserSlice.reducer
